import React from 'react'
import { Typography } from '@mui/material'
import {
  ClearButton, Form, FormField, FormFooter, FormHeader, FormTitle, MovementButton, InfoButton,
} from './BatchesMovement.style'
import { useForm } from 'react-hook-form'
import { AddressLabel } from '../../components/AddressLabel'
import { useDispatch } from 'react-redux'
import { actions } from '../../StockMovementsState'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import { useSelector } from 'src/utils/store'
import { BatchInfoTable } from '../../components/BatchInfoTable'
import { BatchItem } from '../../StockMovementsTypes'
import { BatchesTable } from '../../components/BatchesTable'
import { toast } from 'react-toastify'
import { ModalInfoContent } from '../../components/ModalInfo/ModalInfo'
import { ReplacementConfirmation } from '../../components/ReplacementConfirmation/ReplacementConfirmation'
import { formatDateUTC } from 'src/utils/formatDate'

export const BatchesMovement = () => {
  const dispatch = useDispatch()
  const { fc } = useFulfillmentCenter()
  const [confirmMovementIsOpen, setConfirmMovementIsOpen] = React.useState<boolean>(false)
  const [infoModalIsOpen, setInfoModalIsOpen] = React.useState<boolean>(false)
  const [disabledConfirmButton, setDisabledConfirmButton] = React.useState<boolean>(false)
  const [currentAddress, setCurrentAddress] = React.useState<string>()
  const [hasError, setHasError] = React.useState<boolean>(false)
  const stockMovements = useSelector((state) => state.stockMovements)

  const {
    register, setFocus, getValues, setError, reset, setValue, clearErrors, formState: { errors },
  } = useForm()
  const {
    batchInfo, batchesList, badge, inputAddress,
  } = stockMovements

  const addBatchItem = (addressIndex?: number) => {
    const address = batchInfo?.addresses[addressIndex || 0]

    if (batchInfo && address) {
      const batchItem: BatchItem = {
        batchId: batchInfo.idBatch,
        validity: formatDateUTC(address.dueDate, 'DD/MM/YYYY'),
        quantity: address.quantity,
        address: address.addressFormated,
        idStockBatchDue: address.idStockBatchDue,
        productDescription: batchInfo.productDescription,
      }

      const batchItemWasAdded = batchesList.some((batch) => batch.idStockBatchDue === batchItem.idStockBatchDue)
      setFocus('batchOrAddress')

      if (batchItemWasAdded) {
        toast.error('Esse estoque do lote já foi adicionado!')
        return
      }

      dispatch(actions.setBatchesList([...batchesList, batchItem]))
    }
  }

  const resolveBatchInfoTable = () => (batchInfo && batchInfo.addresses.length > 1) && <BatchInfoTable onRowClick={addBatchItem} />

  const resolveBatchesTable = () => batchesList.length > 0 && <BatchesTable focusAddressOrBatchInput={() => setFocus('batchOrAddress')} />

  const resolveAddressLabel = () => currentAddress && <AddressLabel hasErrors={!!errors.batchOrAddress} address={currentAddress} />

  const typeBadge = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      const value: string = getValues('badge')

      if (!value) {
        setError('badge', { message: 'Crachá deve ser informado!' })
        return
      }

      const onSuccess = () => {
        setFocus('batchOrAddress')
        if (errors.badge) clearErrors('badge')
      }

      dispatch(
        actions.validateSupervisorBadge({
          badge: value,
          onSuccess,
          setError: (message) => setError('badge', { message }),
        }),
      )
    }
  }

  const typeBatchOrAddress = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      const value: string = getValues('batchOrAddress')

      if (!value) {
        setError('batchOrAddress', { message: 'Um endereço ou lote deve ser informado!' })
        return
      }

      const valueIsAddress = value.includes('-')

      if (errors.batchOrAddress) clearErrors('batchOrAddress')
      if (!value) return

      dispatch(actions.setBatchInfo(undefined))

      if (valueIsAddress) {
        const onSuccess = (address: string | undefined) => setCurrentAddress(address)

        dispatch(actions.getAddress({
          fcId: fc.id,
          address: value,
          onSuccess,
          setError: (message) => setError('batchOrAddress', { message }),
        }))
      } else {
        dispatch(actions.getBatchInfo({
          batchId: value,
          fcId: fc.id,
          badge: badge ?? '',
          onSuccess: () => {},
          setError: (message) => setError('batchOrAddress', { message }),
        }))
      }
      setValue('batchOrAddress', '')
    }
  }

  const clearForm = () => {
    dispatch(actions.clearState())
    setFocus('badge')
    reset()
    setHasError(false)
    setCurrentAddress(undefined)
  }

  const resolveMoveBatchesButtonIsDisabled = () => !(!!badge && batchesList.length && !!currentAddress && !errors.batchOrAddress)

  const moveBatches = () => {
    setDisabledConfirmButton(true)
    const movimentations = batchesList.map((batch) => ({ idStockBatchDue: batch.idStockBatchDue, quantity: batch.quantity }))
    const onSuccess = () => {
      setConfirmMovementIsOpen(false)
      clearForm()
      setDisabledConfirmButton(false)
    }

    const onError = () => {
      setHasError(true)
      setDisabledConfirmButton(false)
    }

    dispatch(actions.postMovimentation({
      idAddress: inputAddress?.idLocalizacao!,
      movimentations,
      onSuccess,
      badge: badge ?? '',
      onError,
    }))
  }

  React.useEffect(() => {
    dispatch(actions.clearState())
  }, [dispatch])

  React.useEffect(() => {
    if (!badge) setFocus('badge')
  }, [badge, setFocus])

  if (confirmMovementIsOpen && currentAddress) {
    return confirmMovementIsOpen && (
      <ReplacementConfirmation
        movimentations={batchesList.map((batch) => ({
          description: batch.productDescription,
          quantity: batch.quantity,
          expirationDate: batch.validity,
          inputAddress: currentAddress,
        }))}
        hasError={hasError}
        onFinish={() => {
          setConfirmMovementIsOpen(false)
          clearForm()
        }}
        onCancel={() => setConfirmMovementIsOpen(false)}
        onConfirm={moveBatches}
        disabledConfirmButton={disabledConfirmButton}
      />
    )
  }

  if (infoModalIsOpen) return <ModalInfoContent setIsOpen={setInfoModalIsOpen} />

  return (
    <Form>
      <FormHeader>
        <FormTitle>
          <Typography variant='h6' fontWeight='bold'>Movimentação de lotes</Typography>
        </FormTitle>
        <InfoButton data-testid='info-button' onClick={() => setInfoModalIsOpen(true)} />
      </FormHeader>

      <FormField
        onKeyPress={typeBadge}
        inputProps={{ ...register('badge', { required: true }), 'data-testid': 'badge-input' }}
        label='movimentation.batches.form.badge'
        name='badge'
        testId='badge-input'
        type='password'
        size='medium'
        autoComplete='off'
        error={!!errors.badge}
        helperText={errors.badge?.message}
        fullWidth
        disabled={!!badge}
      />

      <FormField
        onKeyPress={typeBatchOrAddress}
        inputProps={{ ...register('batchOrAddress'), 'data-testid': 'batch-or-address-input' }}
        label='movimentation.batches.form.batch-or-address'
        name='batch-or-address'
        testId='batch-or-address-input'
        type='text'
        size='medium'
        autoComplete='off'
        error={!!errors.batchOrAddress}
        helperText={errors.batchOrAddress?.message}
        fullWidth
        disabled={!badge}
      />

      {resolveBatchInfoTable()}
      {resolveBatchesTable()}
      {resolveAddressLabel()}

      <FormFooter>
        <MovementButton data-testid='movement-button' disabled={resolveMoveBatchesButtonIsDisabled()} type='button' variant='contained' onClick={() => setConfirmMovementIsOpen(true)}>Movimentar</MovementButton>
        <ClearButton data-testid='clear-button' type='button' variant='contained' onClick={clearForm}>Limpar</ClearButton>
      </FormFooter>
    </Form>
  )
}
